import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, Loader2Icon, LockKeyholeIcon } from 'lucide-react';
import { ClaimDocumentsStep } from './calculator/ClaimDocumentsStep';
import { IncidentDetailsStep } from './calculator/IncidentDetailsStep';
import { InjuryWorkStep } from './calculator/InjuryWorkStep';
import { YourDetailsStep } from './calculator/YourDetailsStep';
import { initialCalculatorData, type CalculatorFormData, type CalculatorStatus, type ClaimType } from '../types/claimCalculator';
const claimTypes: {
  value: ClaimType;
  label: string;
}[] = [{
  value: 'road',
  label: 'Road accident'
}, {
  value: 'work',
  label: 'Work accident'
}, {
  value: 'public',
  label: 'Slip, fall or public liability'
}, {
  value: 'other',
  label: 'Other or unsure'
}];
const panelMotion = {
  initial: {
    opacity: 0,
    y: 10
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -8
  },
  transition: {
    duration: 0.2
  }
};
interface CompensationCalculatorProps {
  initialStep?: number;
  initialClaimType?: ClaimType;
}
export function CompensationCalculator({
  initialStep = 0,
  initialClaimType = 'road'
}: CompensationCalculatorProps) {
  const [step, setStep] = useState(Math.min(Math.max(initialStep, 0), 4));
  const [claimType, setClaimType] = useState<ClaimType>(initialClaimType);
  const [data, setData] = useState<CalculatorFormData>(initialCalculatorData);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [status, setStatus] = useState<CalculatorStatus>('idle');
  const [error, setError] = useState('');
  const shouldReduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const hasMounted = useRef(false);
  useEffect(() => {
    if (hasMounted.current) panelRef.current?.focus();else hasMounted.current = true;
  }, [step, status]);
  function update<Key extends keyof CalculatorFormData>(key: Key, value: CalculatorFormData[Key]) {
    setData(current => ({
      ...current,
      [key]: value
    }));
  }
  function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    const invalid = incoming.find(file => file.size > 10 * 1024 * 1024 || !validTypes.includes(file.type));
    if (invalid) {
      setFileError('Please upload PDF, Word, JPG or PNG files up to 10 MB each.');
      return;
    }
    setFileError('');
    setFiles(current => [...current, ...incoming].slice(0, 10));
  }
  function nextStep(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setStep(current => Math.min(current + 1, 4));
  }
  async function submitEstimate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data.name || !data.dateOfBirth || !data.mobile || !data.email) {
      setError('Please add your name, date of birth, mobile number and email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setStatus('submitting');
    await new Promise(resolve => setTimeout(resolve, 900));
    console.log('Compensation estimate request', {
      claimType,
      ...data,
      documents: files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      }))
    });
    setStatus('success');
  }
  const activeMotion = shouldReduceMotion ? {
    initial: false as const,
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0
    }
  } : panelMotion;
  const stepNames = ['What happened?', 'Your details', 'Accident details', 'Injuries and work', 'Claim details and documents'];
  return <div id="compensation-calculator" className="w-full rounded-card border border-white/70 bg-paper p-5 shadow-card-lg" data-id="element-153" data-mp="2s2gm7xzurkqi">
      <div className="flex items-start justify-between gap-4" data-id="element-154" data-mp="2n2c3e1bh2dx4">
        <div data-id="element-155" data-mp="2j3uubc8hqtd2">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-signal" data-id="element-156" data-mp="2j4edew9vnnso">
            Free compensation calculation
          </p>
          <h2 className="mt-1.5 font-serif text-[23px] font-semibold leading-[1.18] text-forest" data-id="element-157" data-mp="3atzm3vykyiob">
            How much is your claim worth?
          </h2>
        </div>
        {step > 0 && status !== 'success' && <span className="shrink-0 rounded-full bg-canvas px-2.5 py-1 text-[12px] font-semibold text-slate" data-id="element-158" data-mp="31tuhtuooo0ed">
            {step}/4
          </span>}
      </div>

      <p className="sr-only" aria-live="polite" aria-atomic="true" data-id="element-159" data-mp="1vaw9dm7l5owv">
        {status === 'success' ? 'Request received' : `Step ${step + 1} of 5: ${stepNames[step]}`}
      </p>

      <div ref={panelRef} tabIndex={-1} className="focus:outline-none" data-id="element-160" data-mp="5uae1c8bpc9s">
        <AnimatePresence mode="wait" data-id="element-161" data-mp="avpyb22ofer4">
          {status === 'success' ? <motion.div key="success" {...activeMotion} className="py-6 text-center" data-id="element-162" data-mp="evqplnavjkfl">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest/10 text-forest" data-id="element-163" data-mp="jnrycz8u2t0v">
                <CheckCircle2Icon className="h-6 w-6" aria-hidden="true" data-id="element-164" data-mp="olthfk69escc" />
              </span>
              <h3 className="mt-4 text-h4 font-semibold text-ink" data-id="element-165" data-mp="tkei1kpfbe1b">
                Your request is with our team
              </h3>
              <p className="mt-2 text-small text-slate" data-id="element-166" data-mp="yioc1rzew8lc">
                Within 24 hours a senior injury lawyer will review your answers
                and call or email you your lump sum payment estimate.
              </p>
            </motion.div> : step === 0 ? <motion.div key="claim-type" {...activeMotion} data-id="element-167" data-mp="34e978inhk4hq">
              <p className="mt-2.5 text-[13px] leading-[1.55] text-slate" data-id="element-168" data-mp="biqwojgcloq4">
                Answer a few simple questions and within 24 hours a senior injury
                lawyer will call or email you your lump sum payment estimate.
              </p>
              <p className="mt-4 text-small font-semibold text-ink" data-id="element-169" data-mp="khufrnfim44m">What happened?</p>
              <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-170" data-mp="ggshp9xc3y48">
                {claimTypes.map(item => <button key={item.value} type="button" aria-pressed={claimType === item.value} onClick={() => setClaimType(item.value)} className={`min-h-[50px] rounded-btn border px-3 py-2 text-left text-[13px] font-semibold leading-[1.3] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal ${claimType === item.value ? 'border-forest bg-forest text-paper' : 'border-ink/10 bg-canvas text-ink hover:border-forest/35 hover:bg-mist'}`} data-id="element-171" data-mp="ugaas1hnxxje">
                    {item.label}
                  </button>)}
              </div>
              <button type="button" onClick={() => setStep(1)} className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-btn bg-signal px-5 text-body font-semibold text-paper shadow-[0_6px_20px_-8px_rgba(188,63,32,0.7)] transition-[transform,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:bg-signal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2" data-id="element-172" data-mp="1iwpq6jtz7ctv">
                Get my free calculation
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-173" data-mp="q7afxwa4xnz0" />
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-[12px] text-slate" data-id="element-174" data-mp="v40vmii12hjw">
                <LockKeyholeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-175" data-mp="1am0qe93vixhl" />
                About one minute · Free · Confidential
              </p>
            </motion.div> : <motion.form key={`${claimType}-${step}`} {...activeMotion} onSubmit={step === 4 ? submitEstimate : nextStep} className="mt-4" data-id="element-176" data-mp="10o4hlnyesemn">
              <div className="max-h-[520px] overflow-y-auto pr-1" data-id="element-177" data-mp="rozmkrm233f8">
                {step === 1 && <YourDetailsStep data={data} update={update} data-id="element-178" data-mp="mx7s45zs4djt" />}
                {step === 2 && <IncidentDetailsStep claimType={claimType} data={data} update={update} data-id="element-179" data-mp="hxmvw3bzujas" />}
                {step === 3 && <InjuryWorkStep claimType={claimType} data={data} update={update} files={files} fileError={fileError} onFilesAdded={addFiles} onFileRemoved={index => setFiles(current => current.filter((_, itemIndex) => itemIndex !== index))} data-id="element-180" data-mp="d14pz04xgewv" />}
                {step === 4 && <ClaimDocumentsStep claimType={claimType} data={data} update={update} files={files} fileError={fileError} onFilesAdded={addFiles} onFileRemoved={index => setFiles(current => current.filter((_, itemIndex) => itemIndex !== index))} data-id="element-181" data-mp="2okkfvezrqrb0" />}
              </div>

              {error && <p role="alert" className="mt-3 text-[12px] font-medium text-signal" data-id="element-182" data-mp="1d7i18ph8cbth">
                  {error}
                </p>}
              <div className="mt-4 flex gap-2" data-id="element-183" data-mp="1i5a3odfi3gbx">
                <button type="button" onClick={() => setStep(current => Math.max(current - 1, 0))} className="flex min-h-[46px] items-center justify-center rounded-btn border border-ink/15 px-3 text-slate transition-colors duration-150 ease-out hover:border-forest hover:text-forest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" aria-label="Back to previous question" data-id="element-184" data-mp="1uf92ph0mugc7">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-185" data-mp="1apf34groewxf" />
                </button>
                <button type="submit" disabled={status === 'submitting'} className="inline-flex min-h-[46px] flex-1 items-center justify-center gap-2 rounded-btn bg-signal px-4 text-small font-semibold text-paper transition-colors duration-150 ease-out hover:bg-signal-600 disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" data-id="element-186" data-mp="1fo05omuehsp1">
                  {status === 'submitting' ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-187" data-mp="kh99lchhlm3v" />
                      Sending…
                    </> : step === 4 ? 'Request my free claim review' : <>
                      Continue
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-188" data-mp="6h81pd2t1eew" />
                    </>}
                </button>
              </div>
              {step === 4 && <p className="mt-3 text-center text-[11px] leading-relaxed text-slate" data-id="element-189" data-mp="3swszq2mv2oep">
                  Free initial review only. Eligibility and value depend on your
                  circumstances and applicable law.
                </p>}
            </motion.form>}
        </AnimatePresence>
      </div>
    </div>;
}